import { openUniversalSearch } from '@fountain/universal-search';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, useLocation, useParams } from 'react-router-dom';

import FountainLogo from '@/assets/icons/fountain-logo.svg';
import SourceLogo from '@/assets/icons/source-logo.svg';
import WxNavbarHost from '@/components/pages/wx-navbar-host';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { Typography } from '@/components/ui/typography';
import { useAuthContext } from '@/contexts/auth-context';
import { useFlag } from '@/flags';
import { useSourceNavItems } from '@/hooks/use-source-nav-items';
import { NavItem } from '@/types/whoami';
import { domainMeta } from '@/utils/domain-meta';

// The UMD navbar does not share Source's router context.
function mountedSourceLink(item: NavItem): NavItem {
  const base = domainMeta.basename;
  if (!base) return item;
  return {
    ...item,
    to:
      typeof item.to === 'string' &&
      item.to.startsWith('/') &&
      !item.to.startsWith('//') &&
      !item.to.startsWith(`${base}/`)
        ? `${base}${item.to}`
        : item.to,
    children: Array.isArray(item.children)
      ? item.children.map(mountedSourceLink)
      : item.children,
  };
}

// Module-level so the memoized navbar host receives the same function on every
// render. The navbar shows its Search button only when this is set, the
// sidebar is expanded, and its own `universal-search-sidebar-button` flag is on.
function openSearchFromSidebar() {
  openUniversalSearch('sidebar');
}

export function AppSidebar() {
  const { pathname } = useLocation();
  const { commitHash } = useParams();
  const navItems = useSourceNavItems();

  // Staging deploys are served under a `/<commitHash>` prefix that the wx-navbar
  // config (sourced from monolith) is unaware of. Strip it so the active-state
  // resolver sees a path it can match against `/<accountSlug>/...`.
  const hostPathname =
    commitHash && pathname.startsWith(`/${commitHash}`)
      ? pathname.slice(`/${commitHash}`.length) || '/'
      : pathname;
  const { account, brandSquareLogoUrl, brandLogoUrl, whoami } =
    useAuthContext();
  const { name } = account ?? {};

  // Memoize navbarConfig to prevent unnecessary re-renders
  // (same pattern as WxCopilotHost)
  const navbarConfig = useMemo(
    () => ({
      locale: whoami?.wx_navbar_config?.locale,
      mainNav: (whoami?.wx_navbar_config?.mainConfig ?? []).map(item =>
        item.key === 'source' ? mountedSourceLink(item) : item,
      ),
      secondaryNav: whoami?.wx_navbar_config?.secondaryConfig ?? [],
      dropdownNav: whoami?.wx_navbar_config?.dropdownConfig ?? [],
      employerDomain: whoami?.wx_navbar_config?.employerDomain,
      employerBasePath: whoami?.wx_navbar_config?.employerBasePath,
      inboxCount: whoami?.wx_navbar_config?.inboxCount,
      disableNavigation: whoami?.wx_navbar_config?.disableNavigation ?? false,
    }),
    [whoami?.wx_navbar_config],
  );

  const isNavigationExcluded = useFlag('unified-navigation-exclusion');
  const showWxNavbar =
    Boolean(whoami?.account_unified_auth) && !isNavigationExcluded;

  const getTypographyVariant = (isActive: boolean) =>
    isActive ? 'body1' : 'subtitle1';

  if (showWxNavbar) {
    return (
      <nav>
        <WxNavbarHost
          userId={whoami?.wx_external_id}
          locale={navbarConfig.locale}
          companyId={whoami?.account_wx_external_id ?? ''}
          inboxCount={navbarConfig.inboxCount}
          employerDomain={navbarConfig.employerDomain}
          employerBasePath={navbarConfig.employerBasePath}
          disableNavigation={navbarConfig.disableNavigation}
          mainConfig={navbarConfig.mainNav}
          secondaryConfig={navbarConfig.secondaryNav}
          dropdownConfig={navbarConfig.dropdownNav}
          hostPathname={hostPathname}
          onSearchClick={openSearchFromSidebar}
        />
      </nav>
    );
  }

  return (
    <Sidebar>
      <SidebarHeader className="border-fx-neutral-200 flex h-[56px] items-start justify-center border-b px-4 [clip-path:inset(0_16px_0_16px)]">
        <img src={FountainLogo} alt="Fountain" className="h-[20px] w-[125px]" />
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <div className="flex items-center gap-2 p-1">
          <img src={SourceLogo} alt="Source" />
          <Typography variant="body2">Source</Typography>
        </div>

        <div className="py-2">
          {navItems.map(item => (
            <NavLink key={item.key} to={item.path}>
              {({ isActive }) => (
                <SidebarMenuButton isActive={isActive}>
                  <span className="w-8" />
                  <Typography variant={getTypographyVariant(isActive)}>
                    <FormattedMessage {...item.label} />
                  </Typography>
                </SidebarMenuButton>
              )}
            </NavLink>
          ))}
        </div>
      </SidebarContent>

      <SidebarFooter className="flex flex-row items-center gap-2">
        {(brandSquareLogoUrl || brandLogoUrl) && (
          <img
            src={brandSquareLogoUrl ?? brandLogoUrl ?? undefined}
            alt="Brand logo"
            className={brandSquareLogoUrl ? 'h-8 w-8' : 'h-8 w-16'}
          />
        )}
        <Typography variant="body1">{name}</Typography>
      </SidebarFooter>
    </Sidebar>
  );
}
